import { Injectable } from "@angular/core";
import { HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { gzip } from "pako";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { LoggingService } from "./logging.service";
import { Constants } from "src/app/shared/constants/constants";
@Injectable({
  providedIn: "root",
})
export class UtilsService {
  httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
      SofyAuth: environment.AUTH_TOKEN,
    }),
  };
  feedbackShown: boolean = false;
  isTutorialRequire: Subject<boolean> = new Subject<boolean>();
  stepActionOverlay: Subject<string> = new Subject<string>();
  assertVariable: Subject<any> = new Subject<any>();
  assertionComponentSelected:  BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  arithmeticEditContext: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  navigationUrl = Constants.NAVIGATION_URL;
  isConditionalMood: boolean = false;
  private lastSetLat$ = new BehaviorSubject<number>(47.93879320472554);
  private lastSetLng$ = new BehaviorSubject<number>(-122.15452806030324);

  constructor(
    private loggingService: LoggingService,
  ) { }

  get getLastSetLat$() { return this.lastSetLat$.asObservable(); }
  get getLastSetLng$() { return this.lastSetLng$.asObservable(); }

  appTransformation(app) {
    let format = "url";
    if (app.AppIcon == null) app.AppIcon = "";
    else if (app.AppIcon.startsWith("data:image")) format = "base64";

    return {
      ...app,
      downloadShow: false,
      install: false,
      selected: false,
      loader: false,
      iconFormat: format,
      downloadPercentage: 0,
      selectedBuildIndex: 0,
    };
  }

  isModalOpen(modalId: string) {
    let modalClass = "";
    if (document.getElementById(modalId)) {
      modalClass = document.getElementById(modalId).style.display;
    }
    let checkIsModalClosed =
      modalClass == "none" || modalClass == "" ? true : false;
    return checkIsModalClosed;
  }

  transformPassword(p) {
    if (p) {
      return p.replace(/./g, "*");
    } else {
      return "";
    }
  }

  transformRealTimeLogs() {
    const logPattern = /(.+?)\s+-+\s+\((\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\)/;
    const logHistory = this.loggingService
      .getLogHistory()
      .map((logEntry: string) => {
        const match = logEntry.match(logPattern);
        if (match && match.length === 3) {
          const logMessage = match[1].trim();
          const timestamp = match[2];
          return `${"("}${timestamp}${")"} ${logMessage}`;
        }
        return logEntry; // If the log entry doesn't match the pattern, keep it as is
      })
      .join("\n");
    return logHistory;
  }
  normalizeBounds(bounds: any): any {
    return this.parseBounds(bounds)
      .split(",")
      .map((n) => parseInt(n, 10)) // here divided by two was written
      .map((n) => Math.ceil(n))
      .join(",");
  }
  parseBounds(bounds: any): string {
    if (!bounds && typeof bounds !== "string") {
      return "";
    }
    bounds = bounds.replaceAll(" ", "");
    bounds = bounds.replace("][", ",");
    bounds = bounds.replace("[", "");
    bounds = bounds.replace("]", "");
    return bounds;
  }
  calculateSrcBounds(bounds: any, imageWidth = 1, deviceWidth = 1): any {
    const imageDeviceRatio =
      imageWidth > deviceWidth
        ? imageWidth / deviceWidth
        : deviceWidth / imageWidth;
    const isMultiply = imageWidth > deviceWidth ? true : false;
    // && imageDeviceRatio < 1.3
    // .map(n => parseInt(n, 10) / (imageDeviceRatio * 1.95))
    if (isMultiply) {
      return bounds
        .split(",")
        .map((n) => parseInt(n, 10) * imageDeviceRatio)
        .map((n) => Math.ceil(n))
        .join(",");
    } else {
      return bounds
        .split(",")
        .map((n) => parseInt(n, 10) / imageDeviceRatio)
        .map((n) => Math.ceil(n))
        .join(",");
    }
  }

  getImageDimension(image: any): Observable<any> {
    return new Observable((observer) => {
      const img = new Image();
      img.onload = (event) => {
        const loadedImage: any = event.currentTarget;
        image.width = loadedImage.width;
        image.height = loadedImage.height;
        observer.next(image);
        observer.complete();
      };
      img.src = image.url;
    });
  }

  async compress(data: any): Promise<any> {
    try {
      const jsonString = JSON.stringify(data);
      const jsonUint8Array = new TextEncoder().encode(jsonString);
      const compressedData = gzip(jsonUint8Array, { level: 9 });
      //converting the compressed data to string for better file upload.
      let convertedString = "";
      for (let i = 0; i < compressedData.length; i++) {
        convertedString += String.fromCharCode(compressedData[i]);
      }
      return convertedString;
    } catch (error) {
      console.log("error compressing!!!");
    }
  }

  // Navigate to Accessibility, Insert Step and Results
  navigateToPortal(path: string) {
    let baseUrl;
    const hostname = window.location.hostname;
    const localHostUrl = this.navigationUrl.LOCALHOST;
    const labUrl = this.navigationUrl.PROD;
    const preProdUrl = this.navigationUrl.PRE_PROD;
    const devTestUrl = this.navigationUrl.DEV_TEST;
    switch (hostname) {
      case "localhost":
        baseUrl = localHostUrl;
        break;
      case "lab.sofy.ai":
        baseUrl = labUrl;
        break;
      case "lab-sofy-stag-01-pre-prod.azurewebsites.net":
        baseUrl = preProdUrl;
        window.open(baseUrl + path, "_blank");
        return;
      default:
        baseUrl = devTestUrl;
    }
    window.open(baseUrl + path, "_blank");
  }

  /* Matches - {lat} | {lng} - that comes in the data message then extracts them to
  update the last set latitude and longitude */
  
  updateLatLng(data: string) {
    const extractedCoordinates = data.match(/(-?\d+\.\d+)\s*\|\s*(-?\d+\.\d+)/);
    if (extractedCoordinates) {
      const latitude = parseFloat(extractedCoordinates[1]);
      const longitude = parseFloat(extractedCoordinates[2]);
      this.lastSetLat$.next(Number(latitude));
      this.lastSetLng$.next(Number(longitude));
    }
  }

  getIconNameViaAction(actionName: string): string {
    switch (actionName) {
      case 'CLICK':
        return 'clickhand';
      case 'ISSUE':
        return 'issueIcon';
      case 'SWIPE':
        return 'swipe';
      case 'ADD_TEST_DATA':
        return 'setTextIcon';
      case 'APP_LAUNCH':
        return 'appLaunch';
      case 'SET_TEXT':
        return 'setTextIcon';
      case 'BACK_KEY':
        return 'backIcon';
      case 'LONG_CLICK':
        return 'LONG_CLICK';
      case 'APP_CRASH_REBOOT':
        return 'appCrashReboot';
      case 'SWIPE_TILL_ELEMENT':
        return 'swipe';
      case 'API_STEP':
        return 'APIStepIcon';
      case 'ARITHMETIC_STEP':
        return 'ARITHMETIC_STEP_COMPARISON';
      case 'UI_VALIDATION_STEP':
        return 'UIValidator';
      case 'PASTE_FROM_CLIPBOARD':
        return 'pasteFromClipboard';  
      default:
        return 'AssertTickIcongreen';
    }
  }

  getSelectedComponent(item: { bounds: Array<number>, component: any, original_bounds: string}, inEdit: boolean = true) {
    let selectedComponent = {
      instance: item.component.instance ? parseInt(item.component?.instance) : 0,
      package: item.component.package || 'no-package',
      type: item.component.type ? item.component.type : 'unknown',
      index: item.component.index ? parseInt(item.component.index) : 0,
      bounds: item.original_bounds || '[-1,-1][-1,-1]',
      focused: item.component.focused
        ? this.parseBool(item.component.focused.toString())
        : false,
      visible: item.component.visible
        ? this.parseBool(item.component.visible.toString())
        : false,
      enabled: item.component.enabled
        ? this.parseBool(item.component.enabled.toString())
        : false,
      focusable: item.component.focusable
        ? this.parseBool(item.component.focusable.toString())
        : false,
      xpathChecksum: item.component.xpathHash
        ? parseInt(item.component.xpathHash) : item.component.xpathChecksum ? item.component.xpathChecksum 
        : -1,
      selected: item.component.selected
        ? this.parseBool(item.component.selected.toString())
        : false,
      clickable: item.component.clickable
        ? this.parseBool(item.component.clickable.toString())
        : false,
      xpath: item.component.xpath || '',
      resourceId: item.component['resource-id'] 
          ? item.component['resource-id'] : item.component.resourceId ? item.component.resourceId
          : '',
      checked: item.component.checked
        ? this.parseBool(item.component.checked.toString())
        : false,
      naf: item.component.naf ? this.parseBool(item.component.naf) : false,
      longClickable: item.component['long-clickable'] || item.component.longClickable || false,
      edittextGroup: false,
      uid:
        item.component.myUID && item.component.myUID > -1
          ? parseInt(item.component.myUID) : item.component.uid ? item.component.uid
          : -1,
      password: item.component.password
        ? this.parseBool(item.component.password.toString())
        : false,
      training: false,
      checkable: item.component.checkable
        ? this.parseBool(item.component.checkable.toString())
        : false,
      scrollable: item.component.scrollable
        ? this.parseBool(item.component.scrollable.toString()) 
        : false,
      parentUID:
        item.component.ParentUID && item.component.ParentUID > -1
          ? parseInt(item.component.ParentUID) : item.component.parentUID ? item.component.parentUID 
          : -1,
      notes: '',
      shortDescription: '',
      vertexStates: [],
      groupId: 0,
      groupPriority: 0,
      prioritySize: 0,
      microVisible: false,
      calculatedActionable: false,
      crawlerTraversal: false,
      calculatedPriority: -1,
      allowManyTraversal: false,
      discoveryStage: null,
      activeFortraversal: true,
      calculatedSize: 0,
      contentDesc: item.component['content-desc'] || item.component.contentDesc || '',
    }
    if (inEdit) {
      return { ...(selectedComponent) as any, clazz: item.component.class || item.component.clazz || 'no-class', text: item.component.text || '' }
    } else {
      return {
        ...(selectedComponent) as any,
        class: item.component.class || 'no-class',
        text: item.component.name || '',
        description: item.component['content-desc']
        ? item.component['content-desc']
        : 'N/A',
      }
    }
  }

  parseBool(str: any): boolean {
    if (typeof str !== 'string') {
      return str === 1 ? true : false;
    } else {
      return str === 'true' ? true : false;
    }
  }

  getBoundArea(b1: string) {
    const a = b1
      .replace("][", ",")
      .replace("[", "")
      .replace("]", "")
      .split(",")
      .map((n) => parseInt(n, 10));

    return (a[2] - a[0]) * (a[3] - a[1]);
  }

  /**
 * 
 * @param mouseEvents - array of click points
 * @param imageWidth 
 * @param imageHeight 
 * @param isScenarioVersionAvailable - now available for all reports (migrated and new)
 * @param isIos 
 * @returns 
 */
  convertMouseEvents(
    mouseEvents: any,
    imageWidth = 1,
    imageHeight = 1,
    isScenarioVersionAvailable = false,
    isIos: boolean = false
  ): any {
    const maxWidth = 270;
    const maxHeight = 480;
    let ratio = 1;
    if (imageWidth > maxWidth) {
      ratio = maxWidth / imageWidth;
    }
    if (imageHeight > maxHeight) {
      ratio = maxHeight / imageHeight;
    }
    if (mouseEvents) {
      for (let i = 0; i < mouseEvents.length; i++) {
        const size = i === 0 || i === mouseEvents.length - 1 ? 6 : 3;

        if (!isScenarioVersionAvailable) {
          mouseEvents[i].point_x = isIos ? mouseEvents[i].point_x : mouseEvents[i].point_x / ratio;
          mouseEvents[i].point_y = isIos ? mouseEvents[i].point_y : mouseEvents[i].point_y / ratio;  
          mouseEvents[i].coords = `${mouseEvents[i].point_x}, ${mouseEvents[i].point_y}, ${size}`;
        } else {
          if(mouseEvents[i]?.coordinates) {
            mouseEvents[i].coordinates[1] = isIos ? mouseEvents[i].coordinates[1] : mouseEvents[i].coordinates[1] / 2;
            mouseEvents[i].coordinates[0] = isIos ? mouseEvents[i].coordinates[0] : mouseEvents[i].coordinates[0] / 2;
            mouseEvents[i].coords = `${mouseEvents[i].coordinates[1]}, ${mouseEvents[i].coordinates[0]}, ${size}`;
          } else {
            mouseEvents[i].point_x = isIos ? mouseEvents[i].point_x : mouseEvents[i].point_x / 2;
            mouseEvents[i].point_y = isIos ? mouseEvents[i].point_y : mouseEvents[i].point_y / 2;
            mouseEvents[i].coords = `${mouseEvents[i].point_x}, ${mouseEvents[i].point_y}, ${size}`;
          }
      
        }
      }
      
      return mouseEvents;
    } else {
      return null;
    }
  }
  calculateSrcBouds(bounds: any, imageWidth = 1, deviceWidth = 1): any {

    const imageDeviceRatio = imageWidth > deviceWidth ? imageWidth / deviceWidth : deviceWidth / imageWidth;
    
    bounds = bounds.split(",");
    if (bounds && bounds != '') {
      if (imageWidth > deviceWidth) {

        return bounds
          .map((coord: string) => parseInt(coord, 10) * imageDeviceRatio)
          .map((coord: number) => Math.ceil(coord))
          .join(',');
      } else if (imageWidth < deviceWidth) {
        return bounds
          .map((coord: string) => parseInt(coord, 10) / imageDeviceRatio)
          .map((coord: number) => Math.ceil(coord))
          .join(',');
      }
      else {
        return bounds
          .map((coord: string) => parseInt(coord, 10))
          .map((coord: number) => Math.ceil(coord))
          .join(',');
      }
    }
    return ''
  }
  convertAndNormalizeBounds(bounds: any): any {
    return this.convertBounds(bounds)
      .split(',')
      .map((n) => parseInt(n, 10)) // here divided by two was written
      .map((n) => Math.ceil(n))
      .join(',');
  }
  convertBounds(bounds: any): string {
    if (!bounds && typeof bounds !== 'string') {
      return '';
    }
    bounds = bounds.replaceAll(' ', '');
    bounds = bounds.replace('][', ',');
    bounds = bounds.replace('[', '');
    bounds = bounds.replace(']', '');
    return bounds;
  }

  //CURL Command Functions
  parseCurlCommand(curlCommand: string): any {
    if (curlCommand.startsWith('curl')) {
      curlCommand = curlCommand.slice(4).trim();
    }

    if (curlCommand.includes('^"')) {
      curlCommand = curlCommand.replace(/\^"/g, '"');     // Unescape ^" to "
      curlCommand = curlCommand.replace(/\\\^/g, '');     // Remove ^ used for line continuation
    }
    curlCommand = curlCommand.replace(/(--\w+)'/g, '$1'); // Remove stray quotes attached to flags like --header'

    // Handle bash ANSI-C quoting: $'...' where \n, \t, etc. are escape sequences
    curlCommand = curlCommand.replace(/\$'((?:[^'\\]|\\[\s\S])*)'/g, (_, inner) => {
      const unescaped = inner
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\r/g, '\r')
        .replace(/\\'/g, "'")
        .replace(/\\\\/g, '\\');
      return `'${unescaped}'`;
    });

    const tokens: string[] = [];
    const regex = /"([^"\\]*(\\.[^"\\]*)*)"|'([^'\\]*(\\.[^'\\]*)*)'|(\S+)/g;
    let match;

    while ((match = regex.exec(curlCommand))) {
      tokens.push(match[1] || match[3] || match[5]);
    }
    let parsedApiData = {
      url: '',
      method: 'GET',
      headers: {},
      body: '{"": ""}',
      pathVariables: [] as { variable: string }[],
      queryParams: [] as { [key: string]: string }[],
      reqBodyType: ''
    };

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];

      switch (token) {
        case '-X':
        case '--request':
          parsedApiData.method = tokens[++i]?.toUpperCase();
          break;

        case '-H':
        case '--header':
          const rawHeader = tokens[++i];
          const [rawKey, ...restHeader] = rawHeader.split(':');
          const key = rawKey.trim();
          const value = restHeader.join(':').trim();
          parsedApiData.headers[key] = value ;
          break;

        case '--data':
        case '--data-raw':
        case '--data-binary':
        case '-d':
          parsedApiData.body = tokens[++i];
          break;

        default:
          if (/^https?:\/\//.test(token)) {
            parsedApiData.url = token;
          } 
      }
    }

    try {
      const urlObj = new URL(parsedApiData.url);

      // Decode URL
      parsedApiData.url = decodeURIComponent(parsedApiData.url);

      // Extract query parameters
      urlObj.searchParams.forEach((val, key) => {
        parsedApiData.queryParams.push({ [decodeURIComponent(key)]: decodeURIComponent(val) });
      });

      // Extract path variable values only
      const segments = urlObj.pathname.split('/').filter(Boolean);
      for (let i = 0; i < segments.length; i++) {
        const segment = decodeURIComponent(segments[i]);

        // This pattern checks for UUID-like values or numeric IDs like '1'
        if (/^[0-9a-fA-F\-]{8,}$/.test(segment) || /^[0-9]+$/.test(segment)) {
          parsedApiData.pathVariables.push({ variable: segment });
        }
      }

      if (parsedApiData.body != '{"": ""}' && parsedApiData.method === 'GET') {
        parsedApiData.method = 'POST'; // in case curl doesn't have the defined http method
      }

      parsedApiData.body = JSON.parse(parsedApiData.body);
      return parsedApiData;
      
    } catch (err) {
      console.error('Unable to parse the URL.', err);
      return parsedApiData;
    }
  }
}
